import * as React from 'react';
import { em, percent, px, important } from 'csx';
import { keyframes, style, classes } from 'typestyle';
import {
  colorBrand,
  colorContrast,
  colorSlate,
  colorSubtle,
  colorWhite,
  colorCrimson,
  colorLightGrey,
} from '../../theme/color';
import { transitionQuickEase } from '../../theme/transition';
import { FormFieldProps } from '../../types/form';
import { DetailedHTMLProps, InputHTMLAttributes } from 'react';

export type FormInputCheckboxProps = FormFieldProps;

const selected = colorBrand.toString();
const focus = colorBrand.toString();
const disabled = colorSubtle.toString();
const fg = colorSlate.toString();
const bg = colorWhite.toString();

const borderscale = keyframes({
  '50%': {
    boxShadow: `0 0 0 ${px(2)} ${selected}`,
  },
});

interface LabelledCheckboxProps extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  isValid?: boolean;
}

export class LabelledCheckbox extends React.Component<LabelledCheckboxProps> {
  render() {
    const { className, children, type, isValid, ...inputProps } = this.props;

    const labelClasses = [LabelledCheckbox.styles.labelContent];
    if (isValid === false) {
      labelClasses.push(LabelledCheckbox.styles.checkboxError);
    }

    return (
      <label className={LabelledCheckbox.styles.checkboxLabel}>
        <input type="checkbox" className={classes(LabelledCheckbox.styles.checkboxInput, className)} {...inputProps} />
        <span className={classes(...labelClasses)}>
          <span>{children}</span>
        </span>
      </label>
    );
  }

  static styles = {
    checkboxLabel: style({
      margin: `${px(10)} 0`,
      display: 'flex',
      alignItems: 'center',
    }),
    checkboxInput: style({
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: px(1),
      margin: px(-1),
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      width: px(1),
      $nest: {
        '&:focus + span:before': {
          borderColor: selected,
          backgroundColor: selected,
        },
        '&:active + span:before': {
          transitionDuration: 0,
        },
        '&:disabled + span:before': {
          borderColor: disabled,
        },
        '&:disabled:focus + span:before': {
          backgroundColor: 'inherit',
        },
        '&:disabled:checked + span:before': {
          backgroundColor: disabled,
        },
        '&:checked:focus + span:before': {
          borderColor: bg,
        },
        '&:not(:checked) + span:before': {
          border: `${px(1)} solid ${colorLightGrey.toString()}`,
          background: colorLightGrey.toString(),
        },

        '&:checked + span:after': {
          content: '""',
          transform: 'rotate(-45deg) scale(1)',
          transition: `transform ${transitionQuickEase}`,
        },
        '&:checked + span:before': {
          animation: `${borderscale} ${transitionQuickEase}`,
          background: selected,
          borderColor: selected,
          boxShadow: 'none',
        },
      },
    }),
    labelContent: style({
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      paddingLeft: px(34),
      lineHeight: em(1.4),
      verticalAlign: 'middle',
      userSelect: 'none',
      fontSize: px(12),
      height: px(24),
      $nest: {
        '&:before': {
          content: '""',
          color: fg,
          position: 'absolute',
          top: 0,
          left: 0,
          width: px(24),
          height: px(24),
          border: `${px(1)} solid ${fg}`,
          textAlign: 'center',
          transition: `all ${transitionQuickEase}`,
        },
        '&:after': {
          boxSizing: 'content-box',
          content: '""',
          position: 'absolute',
          transformOrigin: percent(50),
          transition: `transform ${transitionQuickEase}`,
          backgroundColor: 'transparent',
          top: 'calc(50% - 4px)',
          left: px(6),
          width: px(11),
          height: px(4),
          marginTop: 0,
          borderStyle: 'solid',
          borderColor: bg,
          borderWidth: `0 0 ${px(2)} ${px(2)}`,
          transform: 'rotate(-45deg) scale(0)',
        },
      },
    }),
    checkboxError: style({
      $nest: {
        '&:before': {
          border: important(`${px(2)} solid ${colorCrimson.toString()}`),
        },
      },
    }),
  };
}
